import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface TasteProfile {
  totalInteractions: number;
  topCategories: { name: string; count: number }[];
  topVibes: { name: string; count: number }[];
  topInterests: { name: string; count: number }[];
  tripsPlanned: number;
  sharesCount: number;
  placesSaved: number;
  transportCounts: Record<string, number>;
  timeCounts: Record<string, number>;
  distancePreference: 'near' | 'medium' | 'far' | null;
}

function deriveDistancePreference(
  transportCounts: Record<string, number>,
  timeCounts: Record<string, number>
): 'near' | 'medium' | 'far' | null {
  const total = Object.values(transportCounts).reduce((a, b) => a + b, 0);
  if (total === 0) return null;

  // Score: walking + short time = near, taxi + long time = far, else medium
  let score = 0;
  const transportWeights: Record<string, number> = { walking: -1, transit: 0, taxi: 1 };
  const timeWeights: Record<string, number> = { '1-2-hrs': -1, 'half-day': 0, 'full-day': 0.5, '2-3-days': 1 };

  for (const [transport, count] of Object.entries(transportCounts)) {
    score += (transportWeights[transport] ?? 0) * count;
  }
  for (const [time, count] of Object.entries(timeCounts)) {
    score += (timeWeights[time] ?? 0) * count;
  }

  const normalised = score / total;
  if (normalised <= -0.3) return 'near';
  if (normalised >= 0.3) return 'far';
  return 'medium';
}

export function useTasteProfile(userId: string | null) {
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState<TasteProfile | null>(null);

  useEffect(() => {
    if (!userId || !supabase) {
      setProfile(null);
      return;
    }

    let cancelled = false;
    setLoading(true);

    supabase.rpc('get_taste_profile', { p_user_id: userId })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error || !data) {
          setProfile(null);
        } else {
          const transportCounts = data.transport_counts || {};
          const timeCounts = data.time_counts || {};
          setProfile({
            totalInteractions: data.total_interactions || 0,
            topCategories: data.top_categories || [],
            topVibes: data.top_vibes || [],
            topInterests: data.top_interests || [],
            tripsPlanned: data.trips_planned || 0,
            sharesCount: data.shares_count || 0,
            placesSaved: data.places_saved || 0,
            transportCounts,
            timeCounts,
            distancePreference: deriveDistancePreference(transportCounts, timeCounts),
          });
        }
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [userId]);

  return { loading, profile };
}
